import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";

import { sadieSimulator } from "@/lib/sadieSimulator";
import { cn } from "@/lib/utils";
import {
  SadieSimulatorLineDiff,
  SadieSimulatorPromptDiffSection,
} from "@/types";

const STATUS_STYLES: Record<
  SadieSimulatorPromptDiffSection["status"],
  { label: string | null; border: string; tag: string }
> = {
  SAME: { label: null, border: "border-transparent", tag: "" },
  CHANGED: {
    label: "Changed",
    border: "border-amber-400",
    tag: "bg-amber-100 text-amber-800",
  },
  ADDED: {
    label: "Added",
    border: "border-emerald-500",
    tag: "bg-emerald-100 text-emerald-800",
  },
  REMOVED: {
    label: "Removed",
    border: "border-red-400",
    tag: "bg-red-100 text-red-800",
  },
};

const LINE_STYLES: Record<SadieSimulatorLineDiff["kind"], string> = {
  SAME: "text-slate-800",
  ADDED: "bg-emerald-50 text-emerald-900",
  REMOVED: "bg-red-50 text-red-800 line-through",
};

const TEXT_CLASS = "whitespace-pre-wrap font-mono text-xs font-normal";

// One section of the edited prompt, marked by what the PM's changes did to
// it. A changed one marks its lines too, against the section as built.
export const SadieSimulatorPromptDiffBlock = ({
  section,
}: {
  section: SadieSimulatorPromptDiffSection;
}) => {
  /* Helpers */
  const style = STATUS_STYLES[section.status];

  /* Render */
  return (
    <Column className={cn("gap-1 border-l-2 pl-3", style.border)}>
      <Row className="items-center gap-2">
        <Text className="font-mono text-xs font-semibold text-slate-800">
          {`## ${section.heading}`}
        </Text>
        {style.label ? (
          <Text
            className={cn("rounded px-1.5 text-[10px] font-medium", style.tag)}
          >
            {style.label}
          </Text>
        ) : null}
      </Row>
      <DiffBody section={section} />
    </Column>
  );
};

const DiffBody = ({
  section,
}: {
  section: SadieSimulatorPromptDiffSection;
}) => {
  /* Render */
  const render = () => {
    if (section.status === "REMOVED") {
      return (
        <Text className={cn(TEXT_CLASS, "text-slate-400 line-through")}>
          {section.text}
        </Text>
      );
    }
    if (section.status !== "CHANGED" || section.before === null) {
      return (
        <Text className={cn(TEXT_CLASS, "text-slate-800")}>{section.text}</Text>
      );
    }
    return (
      <Column className="gap-0">
        {sadieSimulator
          .lineDiff(section.before, section.text)
          .map((line, index) => (
            <Text
              className={cn(TEXT_CLASS, LINE_STYLES[line.kind])}
              key={`${line.kind}-${String(index)}`}
            >
              {line.text || " "}
            </Text>
          ))}
      </Column>
    );
  };

  return render();
};
